import { X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';

export type ToastTone = 'info' | 'success' | 'error';

export interface ToastMessage {
  id: number;
  message: string;
  tone: ToastTone;
}

interface ToastStackProps {
  toasts: ToastMessage[];
  onDismiss: (id: number) => void;
}

const toneStyles: Record<ToastTone, string> = {
  info: 'bg-[var(--bg-secondary)] text-[var(--text-primary)] border-[var(--border-default)]',
  success: 'bg-[var(--success)] text-white border-[var(--success)]',
  error: 'bg-[var(--danger)] text-white border-[var(--danger)]',
};

export default function ToastStack({ toasts, onDismiss }: ToastStackProps) {
  return (
    <div className="fixed bottom-10 right-4 z-[120] flex w-[20rem] max-w-[calc(100vw-2rem)] flex-col gap-1.5">
      <AnimatePresence mode="popLayout">
        {toasts.map((toast) => (
          <motion.div
            key={toast.id}
            initial={{ opacity: 0, y: 12, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.97 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            layout
            className={`flex items-start gap-2 rounded-md border px-2.5 py-1.5 shadow-sm ${toneStyles[toast.tone]}`}
            role="status"
            aria-live="polite"
          >
            <p className="flex-1 text-xs leading-4">{toast.message}</p>
            <button
              type="button"
              onClick={() => onDismiss(toast.id)}
              className="rounded p-0.5 text-white/80 hover:text-white"
              aria-label="Dismiss notification"
            >
              <X size={14} />
            </button>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
